import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import * as clipboard from 'clipboard-polyfill';
import { Campaign } from '@interfaces';
import { useTranscript, TranscriptSource, TRANSCRIPT_SOURCE } from '@hooks';
import RenderIf from '@components/RenderIf';
import {
  SvgTranscriptLines,
  SvgScrollDown,
  SvgTarget,
  SvgCopy,
  SvgCheck,
  SvgDownloadTray,
  SvgInfoCircle,
  SvgSparkle,
} from '@assets';
import { formatTimestamp, toPlainText } from './format';
import {
  Panel,
  TabHeader,
  Tab,
  AiBadge,
  SourceHeader,
  SourceIcon,
  SourceLabel,
  SourceInfoButton,
  SourcePopover,
  Toolbar,
  ToolButton,
  ToolSpacer,
  Pill,
  ScrollArea,
  Inner,
  Line,
  Timestamp,
  Text,
  StatePlaceholder,
  Spinner,
  LineState,
} from './styles';

const PanelHeader: FC<{ isAi: boolean }> = ({ isAi }) => (
  <TabHeader>
    <Tab>
      <SvgTranscriptLines />
      <span>Transcript</span>
      <RenderIf condition={isAi}>
        <AiBadge>
          <SvgSparkle size={10} />
          AI
        </AiBadge>
      </RenderIf>
    </Tab>
  </TabHeader>
);

// In tab mode, the SidePanel owns the card and tab header — TranscriptPanel emits only its body.
const Shell: FC<{ embedded: boolean; isAi?: boolean; children: ReactNode }> = ({ embedded, isAi = false, children }) =>
  embedded ? (
    <>{children}</>
  ) : (
    <Panel>
      <PanelHeader isAi={isAi} />
      {children}
    </Panel>
  );

// Own component so toggling the info popover re-renders ~5 elements, not the whole
// cue list the panel holds (which can be hundreds of lines).
const AiSourceHeader: FC = () => {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <SourceHeader>
      <SourceIcon>
        <SvgSparkle size={13} />
      </SourceIcon>
      <SourceLabel>AI-powered transcription</SourceLabel>
      <SourceInfoButton
        type="button"
        onClick={() => setShowInfo((prev) => !prev)}
        aria-label="About AI transcripts"
        title="About AI transcripts"
      >
        <SvgInfoCircle size="14px" />
      </SourceInfoButton>
      <RenderIf condition={showInfo}>
        <SourcePopover role="tooltip">
          This transcript was generated automatically by Sendspark&rsquo;s AI and formatted
          for easy reading. It may contain minor inaccuracies.
        </SourcePopover>
      </RenderIf>
    </SourceHeader>
  );
};

interface Props {
  campaign: Campaign;
  // The <video> element to follow (located by the consumer; lives inside @sendspark/player).
  video: HTMLVideoElement | null;
  // Seek the player to a given time (the player controller's seekTo).
  onSeek: (time: number) => void;
  // Base name for the downloaded .txt file.
  fileName?: string;
  // When true, render only the body (toolbar + lines) without the outer Panel card or "Transcript"
  // tab header — the consumer (SidePanel) owns those when tabs are present.
  embedded?: boolean;
  // When the panel is inside a tab switcher, true while the transcript tab is selected. Used to
  // re-center the active line on tab activation (offsetTop is 0 while display: none). Default true
  // for standalone usage.
  isActive?: boolean;
  // Reports the loaded transcript source so a parent (SidePanel) can badge the right tab.
  onSourceChange?: (source: TranscriptSource) => void;
}

// Once captions stop being "generating" for this long with nothing produced, treat them as
// unavailable (e.g. a video with no audio). Generous so we don't flip a slow job to "unavailable".
const UNAVAILABLE_TIMEOUT_MS = 30000;
// How long to pause auto-scroll after the user scrolls the transcript by hand.
const MANUAL_PAUSE_MS = 4000;
// Window after a programmatic scroll during which scroll events are ignored (not "manual").
const PROGRAMMATIC_WINDOW_MS = 600;

const triggerDownload = (text: string, fileName: string) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${fileName}.txt`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const TranscriptPanel: FC<Props> = ({
  campaign,
  video,
  onSeek,
  fileName = 'transcript',
  embedded = false,
  isActive = true,
  onSourceChange,
}) => {
  const { lines, activeIndex, isLoading, isGenerating, error, source } = useTranscript(campaign, video);
  const isAi = source === TRANSCRIPT_SOURCE.ai;

  const [autoScroll, setAutoScroll] = useState(true);
  const [copied, setCopied] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const programmaticUntilRef = useRef(0);
  const resumeTimerRef = useRef<number | undefined>(undefined);
  const copyTimerRef = useRef<number | undefined>(undefined);

  // Flip to "unavailable" if captions never arrive within the timeout window.
  useEffect(() => {
    if (!isGenerating) {
      setTimedOut(false);
      return undefined;
    }
    const id = window.setTimeout(() => setTimedOut(true), UNAVAILABLE_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [isGenerating]);

  const scrollActiveLineToCenter = useCallback(
    (behavior: ScrollBehavior) => {
      const container = scrollAreaRef.current;
      if (!container || activeIndex < 0) return;
      const el = container.querySelector<HTMLElement>(`[data-line="${activeIndex}"]`);
      if (!el) return;
      const target = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
      programmaticUntilRef.current = Date.now() + PROGRAMMATIC_WINDOW_MS;
      container.scrollTo({ top: Math.max(0, target), behavior });
    },
    [activeIndex],
  );

  // Follow the active line while playing (unless the user recently scrolled by hand).
  useEffect(() => {
    if (!isActive || !autoScroll || pausedRef.current) return;
    scrollActiveLineToCenter('smooth');
  }, [activeIndex, autoScroll, isActive, scrollActiveLineToCenter]);

  // Pause auto-scroll briefly when the user scrolls manually.
  useEffect(() => {
    const container = scrollAreaRef.current;
    if (!container) return undefined;
    const onScroll = () => {
      if (Date.now() < programmaticUntilRef.current) return;
      pausedRef.current = true;
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = window.setTimeout(() => {
        pausedRef.current = false;
      }, MANUAL_PAUSE_MS);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      window.clearTimeout(resumeTimerRef.current);
      window.clearTimeout(copyTimerRef.current);
    };
  }, [isGenerating, isLoading, lines.length]);

  const handleToggleAutoScroll = () => {
    setAutoScroll((prev) => {
      const next = !prev;
      if (next) {
        pausedRef.current = false;
        scrollActiveLineToCenter('smooth');
      }
      return next;
    });
  };

  const handleJumpToNow = () => {
    pausedRef.current = false;
    scrollActiveLineToCenter('smooth');
  };

  const handleCopy = async () => {
    try {
      await clipboard.writeText(toPlainText(lines));
      setCopied(true);
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard can reject (permissions / insecure context) — fail quietly.
    }
  };

  const handleDownload = () => {
    triggerDownload(toPlainText(lines), fileName);
  };

  const showUnavailable =
    !!error || (isGenerating && timedOut) || (!isGenerating && !isLoading && lines.length === 0);
  const showGenerating = !showUnavailable && (isGenerating || isLoading);

  // Report the source only once it's resolved; while loading the parent shows no badge, and a
  // failed/empty transcript ('standard') has no AI source to badge.
  useEffect(() => {
    if (showGenerating) return;
    onSourceChange?.(showUnavailable ? TRANSCRIPT_SOURCE.standard : source);
  }, [showGenerating, showUnavailable, source, onSourceChange]);

  if (showUnavailable) {
    return (
      <Shell embedded={embedded}>
        <StatePlaceholder>Transcript unavailable for this video.</StatePlaceholder>
      </Shell>
    );
  }

  if (showGenerating) {
    return (
      <Shell embedded={embedded}>
        <StatePlaceholder>
          <Spinner />
          Transcript generating…
        </StatePlaceholder>
      </Shell>
    );
  }

  return (
    <Shell embedded={embedded} isAi={isAi}>
      <RenderIf condition={isAi}>
        <AiSourceHeader />
      </RenderIf>
      <Toolbar>
        <ToolButton
          type="button"
          active={autoScroll}
          onClick={handleToggleAutoScroll}
          title="Auto-scroll transcript with the video"
        >
          <SvgScrollDown />
          <span>Auto-scroll</span>
          <Pill on={autoScroll}>{autoScroll ? 'On' : 'Off'}</Pill>
        </ToolButton>

        <ToolButton type="button" onClick={handleJumpToNow} title="Jump to the current moment">
          <SvgTarget />
          <span>Jump to now</span>
        </ToolButton>

        <ToolButton type="button" onClick={handleCopy} title="Copy full transcript">
          {copied ? <SvgCheck /> : <SvgCopy />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </ToolButton>

        <ToolSpacer />

        <ToolButton
           type="button"
           onClick={handleDownload}
           title="Download transcript (.txt)"
           aria-label="Download transcript"
         >
          <SvgDownloadTray />
        </ToolButton>
      </Toolbar>

      <ScrollArea ref={scrollAreaRef}>
        <Inner>
          {lines.map((line, i) => {
            // eslint-disable-next-line no-nested-ternary
            const state: LineState = i < activeIndex ? 'past' : i === activeIndex ? 'active' : 'future';
            return (
              <Line
                // eslint-disable-next-line react/no-array-index-key
                key={`${line.startTime}-${i}`}
                data-line={i}
                type="button"
                state={state}
                onClick={() => onSeek(line.startTime)}
              >
                <Timestamp className="ts" state={state}>
                  {formatTimestamp(line.startTime)}
                </Timestamp>
                <Text state={state}>{line.text}</Text>
              </Line>
            );
          })}
        </Inner>
      </ScrollArea>
    </Shell>
  );
};

export default TranscriptPanel;
